/* HorseLink — Sticky Band logic (Dawn v15)
   Handles Theme Editor dynamic loads + ETA calculation vs 20:00 CET cutoff.
   NL = 1 business day; EU (outside NL) = 2–3 business days.
*/
(function(){
  // --- Utilities (timezone-safe using UTC math on CET parts)
  function getPartsInTZ(timeZone){
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone, hour12:false,
      year:'numeric', month:'2-digit', day:'2-digit',
      hour:'2-digit', minute:'2-digit'
    });
    const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p=>[p.type,p.value]));
    return {
      y: +parts.year, m: +parts.month, d: +parts.day, h: +parts.hour, mi: +parts.minute
    };
  }
  function makeUTC(parts){ return new Date(Date.UTC(parts.y, parts.m-1, parts.d, parts.h, parts.mi, 0)); }
  function dayOfWeekUTC(d){ return d.getUTCDay(); } // 0=Sun..6=Sat
  function addDaysUTC(d, days){ const n=new Date(d); n.setUTCDate(n.getUTCDate()+days); return n; }
  function isWeekendUTC(d){ const w = dayOfWeekUTC(d); return w===0 || w===6; }
  function isHolidayUTC(d, holidayStrs){
    const y=d.getUTCFullYear(), m=String(d.getUTCMonth()+1).padStart(2,'0'), day=String(d.getUTCDate()).padStart(2,'0');
    return holidayStrs.includes(`${y}-${m}-${day}`);
  }
  function nextBusinessDayUTC(d, holidays){
    let n=new Date(d);
    do { n = addDaysUTC(n, 1); } while(isWeekendUTC(n) || isHolidayUTC(n, holidays));
    return n;
  }
  function addBusinessDaysUTC(d, days, holidays){
    let n=new Date(d);
    while(days>0){ n = nextBusinessDayUTC(n, holidays); days--; }
    return n;
  }
  function fmtDateRange(d1, d2, locale='en-GB'){
    const f = new Intl.DateTimeFormat(locale, { weekday:'short', day:'2-digit', month:'short' });
    return d2 ? `${f.format(d1)}–${f.format(d2)}` : f.format(d1);
  }

  // --- Core initializer for one band
  function initBand(band){
    if(!band || band.__hlInit) return;
    band.__hlInit = true;

    // Layout: track height with ResizeObserver
    function setBandHeight(){
      const h = band.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--hl-band-height', `${Math.round(h)}px`);
    }
    const ro = 'ResizeObserver' in window ? new ResizeObserver(setBandHeight) : null;
    if(ro) ro.observe(band);
    setBandHeight();
    window.addEventListener('resize', () => { clearTimeout(window.__hl_r); window.__hl_r = setTimeout(setBandHeight, 100); }, { passive:true });

    // ETA calculation
    const cutoff = (band.dataset.cutoff || '20:00').trim();
    const countryIso = (band.dataset.country || 'NL').toUpperCase();
    const holidays = (band.dataset.holidays || '').split(',').map(s=>s.trim()).filter(Boolean);

    const parts = getPartsInTZ('Europe/Amsterdam');
    const nowCET_UTC = makeUTC(parts);
    const cutoffParts = { y:parts.y, m:parts.m, d:parts.d, h:+cutoff.split(':')[0], mi:+cutoff.split(':')[1] };
    const cutoffCET_UTC = makeUTC(cutoffParts);

    // Is today a business day in CET?
    const todayIsBiz = !(isWeekendUTC(nowCET_UTC) || isHolidayUTC(nowCET_UTC, holidays));

    // Ship date (UTC baseline, but represents CET date)
    let shipDateUTC = new Date(nowCET_UTC);
    if(!(todayIsBiz && nowCET_UTC <= cutoffCET_UTC)) {
      shipDateUTC = nextBusinessDayUTC(nowCET_UTC, holidays);
    }

    // Arrival estimate
    const isNL = countryIso === 'NL';
    const arriveStartUTC = addBusinessDaysUTC(shipDateUTC, isNL ? 1 : 2, holidays);
    const arriveEndUTC   = isNL ? null : addBusinessDaysUTC(shipDateUTC, 3, holidays);

    // Compose message
const shipEl = band.querySelector('[data-hl-eta-ship]');
const arrEl  = band.querySelector('[data-hl-eta-arrival]');
if(shipEl && arrEl){
  const shipText = (todayIsBiz && nowCET_UTC <= cutoffCET_UTC) ? 'ships today' : 'ships next business day';
  shipEl.textContent = `Order by ${cutoff} CET — ${shipText}`;
  arrEl.textContent  = isNL
    ? `ETA ${fmtDateRange(arriveStartUTC)}`
    : `ETA ${fmtDateRange(arriveStartUTC, arriveEndUTC)}`;
}
  }

  // Init on ready + handle Theme Editor dynamic events
  function initAll(){
    document.querySelectorAll('[data-hl-band]').forEach(initBand);
  }
  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', initAll, { once:true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', e => {
    if(e.target && e.target.querySelector) {
      const band = e.target.querySelector('[data-hl-band]');
      if(band) initBand(band);
    }
  });
  document.addEventListener('shopify:section:select', e => {
    if(e.target && e.target.querySelector) {
      const band = e.target.querySelector('[data-hl-band]');
      if(band) initBand(band);
    }
  });
})();

/* ── HL Hero Add-to-Cart — robust (Dawn v15) ───────────────────────── */
(() => {
  // Utilities
  const isVisible = el => !!el && el.offsetParent !== null;

  function getProductForm(preferredSelector) {
    // If a selector was provided on the button, try it first
    if (preferredSelector) {
      const viaAttr = document.querySelector(preferredSelector);
      if (viaAttr) return viaAttr;
    }
    // Otherwise pick the first visible Dawn add-to-cart form
    const candidates = Array.from(document.querySelectorAll('product-form form, form[action*="/cart/add"]'));
    return candidates.find(isVisible) || candidates[0] || null;
  }

  function getNativeAddButton(form) {
    if (!form) return null;
    // Dawn uses a submit button with name="add"
    return (
      form.querySelector('button[type="submit"][name="add"]') ||
      form.querySelector('button[name="add"]') ||
      form.querySelector('input[type="submit"][name="add"]') ||
      form.querySelector('button[type="submit"]') ||
      form.querySelector('input[type="submit"]')
    );
  }

  function getVariantInput(form) {
    return form && form.querySelector('input[name="id"]');
  }

  function scrollToForm(form) {
    try {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_) {
      window.scrollTo(0, form.getBoundingClientRect().top + window.scrollY - 24);
    }
    const focusable =
      form.querySelector('input[type="radio"]:not([disabled]), select:not([disabled])') ||
      form.querySelector('select, input, button');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  // Delegate once on document so it survives section reloads
  if (!window.__HL_ATC_V2__) {
    window.__HL_ATC_V2__ = true;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-hl-add-to-cart]');
      if (!btn) return;

      e.preventDefault();

      // Optional: allow a target selector on the button (not required)
      const targetSelector = btn.getAttribute('data-hl-form-selector') || '';

      const form = getProductForm(targetSelector);
      if (!form) {
        console.warn('[HorseLink] No product form found on the page.');
        return;
      }

      const addBtn = getNativeAddButton(form);
      const variantInput = getVariantInput(form);

      // If Dawn has the add button disabled or no variant selected, guide the user
      const blocked = (addBtn && addBtn.disabled) || !variantInput || !variantInput.value;
      if (blocked) {
        scrollToForm(form);
        return;
      }

      // Trigger the native add flow so Dawn opens the cart drawer/notification
      if (addBtn) {
        addBtn.click();
      } else if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });

    // No-ops, but keep hooks so we initialize once per page view
    document.addEventListener('shopify:section:load', () => {});
    document.addEventListener('shopify:section:select', () => {});
  }
})();

/* ───────────────────────────────────────────────────────────
   HL — Science snap hint + Routine modal
   (Extends existing event wiring; does not touch ETA logic)
   ─────────────────────────────────────────────────────────── */

(function () {
  const SELECTORS = {
    snapContainers: '[data-hl-snap]',
    modalOpen: '[data-hl-modal-open]',
    modalClose: '[data-hl-modal-close]',
    modal: '[data-hl-modal]'
  };

  function initSnapHints(root = document) {
    root.querySelectorAll(SELECTORS.snapContainers).forEach((el) => {
      const needsHint = el.scrollWidth > el.clientWidth + 8;
      if (needsHint) el.setAttribute('data-hl-snap-hint', 'true');
      else el.removeAttribute('data-hl-snap-hint');
    });
  }

  function initModals(root = document) {
    root.querySelectorAll(SELECTORS.modalOpen).forEach((btn) => {
      btn.removeEventListener('click', onOpen); // idempotent
      btn.addEventListener('click', onOpen);
    });

    function onOpen(e) {
      const targetId = e.currentTarget.getAttribute('data-hl-modal-open');
      const dlg = document.getElementById(targetId);
      if (!dlg || typeof dlg.showModal !== 'function') return;
      dlg.showModal();

      const vid = dlg.querySelector('video');
      if (vid) { try { vid.play().catch(() => {}); } catch (_) {} }

      // close wiring
      dlg.addEventListener('click', onBackdrop);
      dlg.querySelectorAll(SELECTORS.modalClose).forEach((c) => {
        c.addEventListener('click', () => closeDialog(dlg), { once: true });
      });
      dlg.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape') closeDialog(dlg);
      }, { once: true });
    }

    function onBackdrop(ev) {
      if (ev.target === ev.currentTarget) closeDialog(ev.currentTarget);
    }

    function closeDialog(dlg) {
      const vid = dlg.querySelector('video');
      if (vid) { try { vid.pause(); } catch (_) {} }
      if (typeof dlg.close === 'function') dlg.close();
    }
  }

  // Initial run
  document.addEventListener('DOMContentLoaded', () => {
    initSnapHints();
    initModals();
  });

  // Re-init when sections mount/select in Theme Editor
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target;
    initSnapHints(root);
    initModals(root);
  });
  document.addEventListener('shopify:section:select', (e) => {
    const root = e.target;
    initSnapHints(root);
    initModals(root);
  });
})();

/* ── HL Science carousel (center-snap + dots + active state) ── */
(function () {
  function initScienceCarousel(root = document) {
    root.querySelectorAll('[data-hl-carousel]').forEach((section) => {
      const track = section.querySelector('.hl-science__list');
      if (!track) return;

      const cards = Array.from(track.querySelectorAll('.hl-science__card'));
      const dots  = Array.from(section.querySelectorAll('[data-hl-dot]'));

      // Helpers
      const setActive = (idx) => {
        dots.forEach((d,i) => {
          const active = i === idx;
          d.classList.toggle('is-active', active);
          d.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        cards.forEach((c,i) => c.classList.toggle('is-active', i === idx));
      };

      const getIndexFromScroll = () => {
        const mid = track.scrollLeft + track.clientWidth / 2;
        let closest = 0, best = Infinity;
        cards.forEach((c, i) => {
          const center = c.offsetLeft + c.clientWidth / 2;
          const dist = Math.abs(center - mid);
          if (dist < best) { best = dist; closest = i; }
        });
        return closest;
      };

      const scrollToIndex = (i) => {
        const card = cards[i];
        if (!card) return;
        const left = card.offsetLeft + card.clientWidth / 2 - track.clientWidth / 2;
        track.scrollTo({ left, behavior: 'smooth' });
        setActive(i);
      };

      // Init active (center the first visible card)
      setActive(getIndexFromScroll());

      // Update active while scrolling (debounced with rAF)
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => setActive(getIndexFromScroll()));
      });

      // Dots
      dots.forEach((d, i) => d.addEventListener('click', () => scrollToIndex(i)));

      // Keep centered on resize
      window.addEventListener('resize', () => {
        const i = getIndexFromScroll();
        setActive(i);
        scrollToIndex(i);
      }, { passive: true });
    });
  }

  document.addEventListener('DOMContentLoaded', () => initScienceCarousel());
  document.addEventListener('shopify:section:load', (e) => initScienceCarousel(e.target));
  document.addEventListener('shopify:section:select', (e) => initScienceCarousel(e.target));
})();

/* ==========================================================
   HL Carousel + Drawer (Discipline Highlights)
   - Keeps ETA logic untouched
   - Initializes on DOMContentLoaded and Shopify section events
   ========================================================== */
(function () {
  const qs = (root, sel) => Array.from(root.querySelectorAll(sel));
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function initCarousel(root) {
    if (!root || root.__hlCarouselReady) return;
    root.__hlCarouselReady = true;

    const track = root.querySelector('[data-hl-track]');
    const slides = qs(root, '[data-hl-slide]');
    const rail = root.querySelector('[data-hl-rail]') || root.querySelector('.hl-highlight__rail');
    const tabs = rail ? qs(rail, '[role="tab"]') : [];

    // --- Helper: select slide by index
    const selectByIndex = (i, focusTab = false, updateHash = true) => {
      const idx = clamp(i, 0, slides.length - 1);
      const slide = slides[idx];
      if (!slide) return;

      // Make others inert
      slides.forEach((s, j) => {
        const hidden = j !== idx;
        s.toggleAttribute('hidden', false); // keep all mounted for snap/drag
      });

      // Scroll into view
      slide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

      // Tab states
      tabs.forEach((t, j) => {
        const sel = j === idx;
        t.setAttribute('aria-selected', sel);
        t.setAttribute('tabindex', sel ? '0' : '-1');
        if (sel && focusTab) t.focus({ preventScroll: true });
      });

      // Hash (deep-link)
      if (updateHash) {
        const handle = slide.getAttribute('data-hl-handle');
        if (handle) history.replaceState(null, '', `#${handle}`);
      }
    };

    // --- Click tabs to select
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => selectByIndex(i, false, true));
      tab.addEventListener('keydown', (e) => {
        const k = e.key;
        if (k === 'ArrowRight' || k === 'ArrowLeft') {
          e.preventDefault();
          const dir = k === 'ArrowRight' ? 1 : -1;
          selectByIndex(i + dir, true, true);
        }
      });
    });

    // --- Track: update selected tab on manual swipe
    let raf = null;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const x = track.scrollLeft;
        const w = track.clientWidth;
        // nearest slide
        let nearest = 0, dist = Infinity;
        slides.forEach((s, i) => {
          const d = Math.abs(s.offsetLeft - x);
          if (d < dist) { dist = d; nearest = i; }
        });
        const curr = tabs.findIndex(t => t.getAttribute('aria-selected') === 'true');
        if (nearest !== curr) selectByIndex(nearest, false, true);
      });
    };

    if(track){
    track.addEventListener('scroll', onScroll, { passive: true });

    }

    // --- Drawer toggles
    qs(root, '[data-hl-drawer-toggle]').forEach(btn => {
      const id = btn.getAttribute('data-hl-drawer-toggle');
      const drawer = root.querySelector(`#drawer-${id}`);
      if (!drawer) return;

      const closeBtn = drawer.querySelector('[data-hl-drawer-close]');

      const open = () => {
        drawer.hidden = false;
        drawer.setAttribute('aria-hidden', 'false');
        btn.setAttribute('aria-expanded', 'true');
        // move focus to first focusable
        const focusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        (focusable || drawer).focus({ preventScroll: true });
      };
      const close = () => {
        drawer.hidden = true;
        drawer.setAttribute('aria-hidden', 'true');
        btn.setAttribute('aria-expanded', 'false');
        btn.focus({ preventScroll: true });
      };

      btn.addEventListener('click', () => {
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        isOpen ? close() : open();
      });
      closeBtn && closeBtn.addEventListener('click', close);
      drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    });

    // --- Clip play/pause toggles
    qs(root, '[data-hl-clip-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const wrap = btn.closest('.hl-drawer__video');
        const v = wrap && wrap.querySelector('[data-hl-clip]');
        if (!v) return;
        if (v.paused) { v.play().catch(() => {}); btn.textContent = 'Pause'; }
        else { v.pause(); btn.textContent = 'Watch 20s routine'; }
      });
    });

    // --- Auto-contrast helper: if image is bright, flip overlay style
    const firstSlide = slides[0];
    if (firstSlide) {
      const img = firstSlide.querySelector('.hl-highlight__img');
      if (img && 'decode' in img) {
        img.decode().then(() => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            canvas.width = 16; canvas.height = 16;
            ctx.drawImage(img, 0, 0, 16, 16);
            const data = ctx.getImageData(0, 0, 16, 16).data;
            let luma = 0;
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i], g = data[i+1], b = data[i+2];
              luma += 0.2126*r + 0.7152*g + 0.0722*b;
            }
            luma = luma / (data.length / 4);
            if (luma > 180) root.classList.add('hl-highlight--dark');
          } catch (e) { /* ignore */ }
        });
      }
    }

    // --- Hash deep link
    const allHandles = slides.map(s => s.getAttribute('data-hl-handle')).filter(Boolean);
    const fromHash = () => {
      const h = (location.hash || '').replace('#','');
      const i = allHandles.indexOf(h);
      if (i >= 0) selectByIndex(i, false, false);
    };
    fromHash();
  }

  function bootAll(scope = document) {
    qs(scope, '[data-hl-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootAll());
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', (e) => bootAll(e.target));
  document.addEventListener('shopify:section:select', (e) => bootAll(e.target));
})();

/* HL — Discipline highlight slider */
(function(){
  function initDiscipline(root){
    if (!root || root.dataset.hlReady === '1') return;
    root.dataset.hlReady = '1';

    const slides   = Array.from(root.querySelectorAll('.hl-highlight__slide'));
    const previews = Array.from(root.querySelectorAll('.hl-preview'));
    const interval = parseInt(root.dataset.interval || '6000', 10);
    let autoplay   = root.dataset.autoplay === 'true';
    let idx = 0, raf = null, t0 = 0;

    function setActive(i){
      if (i < 0) i = slides.length - 1;
      if (i >= slides.length) i = 0;
      slides.forEach((el,n)=> n===i ? el.removeAttribute('hidden') : el.setAttribute('hidden',''));
      previews.forEach((b,n)=> b.setAttribute('aria-selected', n===i ? 'true' : 'false'));
      idx = i;
      t0 = performance.now();
      updateDots(0); // reset progress bar
    }

    function currentDotsWrap(){
      const slide = slides[idx];
      return slide ? slide.querySelector('.hl-ctrl__dots') : null;
    }

    function updateDots(pct){
      const wrap = currentDotsWrap();
      if (!wrap) return;
      const dots = wrap.querySelectorAll('.hl-ctrl__dot');
      const active = dots[idx];
      if (!active) return;
      const r = active.getBoundingClientRect();
      const c = wrap.getBoundingClientRect();
      wrap.style.setProperty('--bar-x', (r.left - c.left) + 'px');
      wrap.style.setProperty('--bar-w', Math.max(0, Math.min(1,pct)) * r.width + 'px');
    }

    function frame(now){
      const pct = (now - t0) / interval;
      updateDots(pct);
      if (autoplay && pct >= 1) { setActive(idx + 1); }
      if (autoplay) raf = requestAnimationFrame(frame);
    }

    function play(){
      autoplay = true; t0 = performance.now();
      updateToggleUI();
      cancelAnimationFrame(raf); raf = requestAnimationFrame(frame);
    }
    function pause(){
      autoplay = false; updateToggleUI();
      cancelAnimationFrame(raf); raf = null;
    }
    function updateToggleUI(){
      root.querySelectorAll('.hl-ctrl__toggle').forEach(btn => {
        btn.setAttribute('aria-pressed', autoplay ? 'true' : 'false');
        btn.setAttribute('aria-label', autoplay ? 'Pause autoplay' : 'Play');
        btn.innerHTML = autoplay
          ? '<svg class="hl-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5h3v14H8zm5 0h3v14h-3z"/></svg>'
          : '<svg class="hl-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 5l10 7-10 7V5z"/></svg>';
      });
    }

    // Clickable previews
    previews.forEach(btn => {
      btn.addEventListener('click', () => {
        const i = parseInt(btn.getAttribute('data-hl-slide'), 10) || 0;
        setActive(i);
      });
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); btn.click(); }
      });
    });

    // Clickable dots (inside any slide)
    root.querySelectorAll('.hl-ctrl__dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const i = parseInt(dot.getAttribute('data-hl-dot-index'), 10) || 0;
        setActive(i);
      });
    });

    // Play/Pause buttons
    root.querySelectorAll('.hl-ctrl__toggle').forEach(btn => {
      btn.addEventListener('click', () => autoplay ? pause() : play());
    });

    // Resize = recompute bar position
    window.addEventListener('resize', () => updateDots(0), { passive:true });

    // Theme Editor hooks
    document.addEventListener('shopify:section:select', (ev) => {
      if (root.contains(ev.target)) pause();
    });
    document.addEventListener('shopify:section:deselect', (ev) => {
      if (root.contains(ev.target) && root.dataset.autoplay === 'true') play();
    });

    // Init
    setActive(0);
    updateToggleUI();
    if (autoplay) play();
  }

  function boot(){
    document.querySelectorAll('[data-section-type="hl-discipline"]').forEach(initDiscipline);
  }
  if (document.readyState !== 'loading') boot();
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('[data-section-type="hl-discipline"]');
    if (el) initDiscipline(el);
  });
})();


// ——— Routine timeline (drawers) ——————————————————————————
(function () {
  const openers = document.querySelectorAll('[data-hl-drawer]');
  openers.forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-hl-drawer');
      const dlg = document.getElementById(`hl-drawer-${id}`);
      if (!dlg) return;
      if (typeof dlg.showModal === 'function') {
        dlg.showModal();
      } else {
        dlg.setAttribute('open', 'open'); // fallback
      }
      // focus close for keyboard users
      const closer = dlg.querySelector('[data-hl-drawer-close]');
      closer && closer.focus();
    });
  });

  document.addEventListener('click', (e) => {
    const closer = e.target.closest('[data-hl-drawer-close]');
    if (!closer) return;
    const dlg = closer.closest('dialog.hl-modal');
    if (dlg && typeof dlg.close === 'function') dlg.close();
    else if (dlg) dlg.removeAttribute('open');
  });

  // Close on ESC fallback (dialog handles this natively when supported)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('dialog.hl-modal[open]').forEach(dlg => {
      if (typeof dlg.close === 'function') dlg.close();
      else dlg.removeAttribute('open');
    });
  });
})();
